import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, homeFor } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import SidiLogo, { SidiWordmark } from "@/components/SidiLogo";
import { useBrand, brandUrl } from "@/hooks/useBrand";
import BrandWatermark from "@/components/BrandWatermark";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Login() {
  const { login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const brand = useBrand();
  const hero = brandUrl("hero", brand) || brandUrl("logo", brand) || "https://images.unsplash.com/photo-1679429688722-9390263710c2?crop=entropy&cs=srgb&fm=jpg&q=85";
  const portfolio = brandUrl("portfolio", brand);

  const [fpOpen, setFpOpen] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpBusy, setFpBusy] = useState(false);
  const sendForgot = async () => {
    if (!fpEmail.trim()) return toast.error("Saisissez votre adresse email");
    setFpBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fpEmail.trim() });
      toast.success(data.message || "Email envoyé si le compte existe");
      setFpOpen(false); setFpEmail("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setFpBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const u = await login(email, password);
      toast.success("Accès autorisé — Bienvenue");
      nav(homeFor(u.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Identifiants incorrects");
    }
  };

  return (
    <div className="min-h-screen flex items-stretch relative">
      <BrandWatermark logoOpacity={0.04} />
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 relative overflow-hidden">
        <div className="absolute inset-0" style={{
          backgroundImage: `url(${hero})`,
          backgroundSize: "cover", backgroundPosition: "center", opacity: 0.45
        }} />
        <div className="absolute inset-0" style={{
          background: "linear-gradient(120deg, #050505 25%, rgba(5,5,5,0.55) 60%, rgba(5,5,5,0.92) 100%)"
        }} />
        <div className="relative z-10">
          <div className="flex items-center gap-4">
            <SidiWordmark height={96} />
            <div>
              <div className="text-xs tracking-[0.35em] uppercase text-[var(--text-secondary)]">Chauffeurs privés · Grande Remise</div>
              <div className="font-serif-lux text-lg gold-gradient-text">Cockpit IA</div>
            </div>
          </div>
        </div>
        <div className="relative z-10">
          <div className="text-xs tracking-[0.3em] uppercase text-[var(--gold)] mb-4">Parce que chaque détail compte…</div>
          <h1 className="font-serif-lux text-5xl xl:text-6xl leading-[1.05] mb-6">
            L'orchestre invisible<br/>de votre <span className="gold-gradient-text">flotte de luxe.</span>
          </h1>
          <p className="text-[var(--text-secondary)] max-w-md text-base leading-relaxed">
            « La grandeur d'un métier réside dans la passion avec laquelle il est exercé, car c'est dans l'amour du service que naît l'excellence. »
          </p>
          {portfolio && <a href={portfolio} target="_blank" rel="noreferrer" data-testid="portfolio-link" className="inline-block mt-6 text-[11px] tracking-[0.25em] uppercase text-[var(--gold-light)] border-b border-[var(--gold-dark)] pb-1 hover:text-[var(--gold)]">Découvrir le portfolio Sidi →</a>}
        </div>
        <div className="relative z-10 flex gap-6 text-[11px] tracking-[0.25em] uppercase text-[var(--text-muted)]">
          <span>Licence EVTC078240650</span>
          <span>·</span>
          <span>contact@sidiparis.com</span>
          <span>·</span>
          <span>Voice First</span>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="lg:hidden flex justify-center mb-8"><SidiWordmark height={120} /></div>
          <div className="mb-10">
            <div className="text-xs tracking-[0.3em] uppercase text-[var(--gold)] mb-3">Accès Cockpit</div>
            <h2 className="font-serif-lux text-4xl mb-2">Bonsoir.</h2>
            <p className="text-[var(--text-secondary)]">Vos équipes, vos chauffeurs, vos clients — tous sous votre commandement.</p>
          </div>

          <div className="space-y-5">
            <div>
              <Label className="text-[11px] tracking-[0.2em] uppercase text-[var(--text-secondary)]">Identifiant</Label>
              <Input
                data-testid="login-email"
                name="email" autoComplete="username"
                type="email" value={email} onChange={e=>setEmail(e.target.value)}
                className="mt-2 bg-[var(--surface)] border-[var(--border-subtle)] focus-visible:ring-[var(--gold)] focus-visible:border-[var(--gold)] h-12"
                required
              />
            </div>
            <div>
              <Label className="text-[11px] tracking-[0.2em] uppercase text-[var(--text-secondary)]">Mot de passe</Label>
              <Input
                data-testid="login-password"
                name="password" autoComplete="current-password"
                type="password" value={password} onChange={e=>setPassword(e.target.value)}
                className="mt-2 bg-[var(--surface)] border-[var(--border-subtle)] focus-visible:ring-[var(--gold)] focus-visible:border-[var(--gold)] h-12"
                required
              />
            </div>
            <div className="text-right -mt-2">
              <button type="button" onClick={() => setFpOpen(true)} data-testid="forgot-password-link" className="text-[11px] tracking-wider uppercase text-[var(--text-muted)] hover:text-[var(--gold-light)]">Mot de passe oublié ?</button>
            </div>

            <Button
              data-testid="login-submit"
              type="submit" disabled={loading}
              className="w-full h-12 mt-4 font-medium tracking-wider uppercase text-xs"
              style={{ background: "linear-gradient(135deg,#D9C7A0,#B89F78 50%,#7E6A48)", color: "#050505" }}
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? "Authentification…" : "Entrer dans le cockpit"}
            </Button>

            <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]"><span className="flex-1 h-px bg-[var(--border-subtle)]" />ou<span className="flex-1 h-px bg-[var(--border-subtle)]" /></div>
            <Button
              type="button" variant="outline" data-testid="google-login-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/cockpit";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full h-12 border-[var(--border-medium)] bg-transparent hover:bg-[var(--surface)] text-xs tracking-wider uppercase"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.6 3.9-5.5 3.9-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.4 6.7 2.4 12s4.3 9.6 9.6 9.6c5.5 0 9.2-3.9 9.2-9.4 0-.6-.1-1.1-.2-1.6H12z"/></svg>
              Continuer avec Google
            </Button>

            <div className="text-[11px] text-[var(--text-muted)] tracking-wider text-center pt-4 hairline-top">
              Accès réservé — SIDI PARIS
            </div>
          </div>
        </form>
      </div>

      <Dialog open={fpOpen} onOpenChange={setFpOpen}>
        <DialogContent className="bg-[var(--surface)] border-[var(--border-medium)] max-w-md" data-testid="forgot-dialog">
          <DialogHeader><DialogTitle className="font-serif-lux text-[var(--gold-light)]">Mot de passe oublié</DialogTitle></DialogHeader>
          <p className="text-sm text-[var(--text-secondary)]">Saisissez votre adresse email. Si un compte existe, vous recevrez un lien pour choisir un nouveau mot de passe.</p>
          <Input type="email" value={fpEmail} onChange={e => setFpEmail(e.target.value)} placeholder="vous@exemple.com" className="h-11" data-testid="forgot-email" onKeyDown={e => e.key === "Enter" && sendForgot()} />
          <Button onClick={sendForgot} disabled={fpBusy} className="w-full h-11 tracking-wider uppercase text-xs" style={{ background: "linear-gradient(135deg,#D9C7A0,#B89F78 50%,#7E6A48)", color: "#050505" }} data-testid="forgot-submit">
            {fpBusy ? "Envoi…" : "Recevoir le lien"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
